import {
  Badge,
  Box,
  Button,
  Divider,
  Grid,
  GridItem,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  Tooltip,
  useDisclosure,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  type ArchivedProjectDataFragment,
  type FinishedProjectDataFragment,
  ProjectStatusEnum,
} from "@src/__generated__/graphql";
import type { CompleteExpenseBulkDataFragment } from "@src/__generated__/urql-graphql";
import { GraphCard } from "@src/components/modules/projects/detail/GraphCard";
import ArchivedBadge from "@src/components/modules/projects/detail/Header/ArchivedBadge";
import { BillableBadge } from "@src/components/modules/projects/detail/Header/BillableBadge";
import EditButton from "@src/components/modules/projects/detail/Header/EditButton";
import FinishedBadge from "@src/components/modules/projects/detail/Header/FinishedBadge";
import PurchaseOrderBadge from "@src/components/modules/projects/detail/Header/PurchaseOrderBadge";
import { UserCard } from "@src/components/modules/projects/detail/Header/UserCard";
import {
  Avatar,
  BrandLogo,
  ButtonLink,
  Card,
  formatDate,
  InputDatePicker,
  Link,
  ModalConfirm,
  Select,
  UserTooltip,
} from "@src/components/ui-kit";
import { GoogleDriveLink } from "@src/components/ui-kit/GoogleDriveLink";
import { Icon } from "@src/components/ui-kit/Icon";
import { SharepointLink } from "@src/components/ui-kit/SharepointLink";
import { TextEditor } from "@src/components/ui-kit/TextEditor/TextEditor";
import { NavigationProject } from "@src/components/widgets/AppSubNav";
import { ModalOrder } from "@src/components/widgets/Modals/ModalOrder";
import {
  PopoverProjectActions,
  type PopoverProjectActionsProps,
} from "@src/components/widgets/PopoverProjectActions";
import { Can, can } from "@src/utils/components/permissions";
import { toApiDate } from "@src/utils/dates";
import { useStore as useAppStore } from "@src/utils/hooks";
import { getQueryParamOptional } from "@src/utils/routing";
import { TeamSelect } from "@src/widgets/TeamSelect";
import { UserSelect } from "@src/widgets/UserSelect";
import { runInAction } from "mobx";
import { observer } from "mobx-react-lite";
import { useRouter } from "next/router";
import { Fragment, useEffect } from "react";
import { PROJECT_NOTE_PREFIX, useStore } from "../Store";

const Header = () => {
  const { pipedriveStore } = useAppStore();
  const store = useStore();
  const createPOModal = useDisclosure();
  const noteModalDisclosure = useDisclosure();

  useEffect(() => {
    pipedriveStore.fetchOptions();
  }, []);

  const router = useRouter();

  const onCompleteOurWork: PopoverProjectActionsProps["onCompleteOurWork"] = (
    ourWorkItems,
  ) => {
    const items = store.project?.ourWorkOverview.flatMap((i) => i.budgetItems);

    store.refetchGraph?.();

    if (items) {
      items.forEach((i) => {
        const affected = ourWorkItems.find((j) => j.id === i.id);

        if (affected?.lastWorkInProgressItemStat) {
          i.setProgressPercent(
            affected.lastWorkInProgressItemStat.progress_percent,
          );
        }
      });

      store.project?.setLastWorkInProgressPercent(100 * 100);
    }
  };

  const onCompleteExpense = (
    expenseItems: CompleteExpenseBulkDataFragment[],
  ) => {
    const items = store.project?.expensesStructured.flatMap(
      (i) => i.budgetItems,
    );

    if (items) {
      items.forEach((i) => {
        const affected = expenseItems.find((j) => j.id === i.id);

        if (affected) {
          i.updateCompletedAt(
            affected.completed_at
              ? toApiDate(affected.completed_at)
              : undefined,
          );
        }
      });
    }
  };

  const onArchived = (project: ArchivedProjectDataFragment) => {
    runInAction(() => {
      if (store.project) {
        store.project.status = project.status;
        store.project.archived_at = project.archived_at;
        store.project.archivedBy = project.archivedBy;
      }
    });
  };

  const onFinished = (project: FinishedProjectDataFragment) => {
    runInAction(() => {
      if (store.project) {
        store.project.status = project.status;
        store.project.finished_at = project.finished_at;
        store.project.finishedBy = project.finishedBy;
      }
    });
  };

  if (!store.project) {
    return null;
  }

  return (
    <Card data-testid="project-header" overflow="hidden" mb="8">
      <HStack
        align="center"
        justify="space-between"
        px="4"
        borderBottom="1px"
        borderBottomColor="gray.200"
      >
        <NavigationProject id={store.project.id} />

        <HStack align="center" spacing="2">
          {(can("project_update_all", "project_update_team") ||
            can({
              do: "project_update_own",
              eval: (user) =>
                store.project ? store.project.userIds.includes(user.id) : false,
            })) &&
          !store.project.isTemplate ? (
            <EditButton
              projectId={store.project.id}
              w={{ base: "full", md: undefined }}
            />
          ) : (
            <ButtonLink
              href={{
                pathname: "/projects/create",
                query: {
                  fromTemplateId: store.project.id,
                },
              }}
              leftIcon={<Icon name="plus" />}
            >
              <Trans>Create project</Trans>
            </ButtonLink>
          )}

          <Can
            do={[
              "project_archive_all",
              "project_archive_own",
              "project_archive_team",
              "project_finish_all",
              "project_finish_own",
              "project_finish_team",
              "project_delete_all",
              "project_delete_own",
              "project_delete_team",
              "project_duplicate_all",
              "project_duplicate_own",
              "project_duplicate_team",
              "project_update_own",
              "project_update_all",
              "project_update_team",
              "requestForInvoicing_create_all",
              "requestForInvoicing_create_own",
              "requestForInvoicing_create_team",
            ]}
          >
            <PopoverProjectActions
              project={store.project}
              openModalOnMount={
                getQueryParamOptional("action", router.query) ===
                "close_project"
                  ? "finish"
                  : undefined
              }
              hideActions={["edit", "pin-unpin"]}
              onCompleteOurWork={onCompleteOurWork}
              onCompleteExpense={onCompleteExpense}
              onCreditRedistributed={store.refetchProject}
              onArchived={onArchived}
              onFinished={onFinished}
              activator={(isLoading) => (
                <Button
                  w="full"
                  colorScheme="grey"
                  isLoading={isLoading}
                  rightIcon={<Icon name="chevron-down" />}
                  variant="outline"
                >
                  <Trans>Actions</Trans>
                </Button>
              )}
            />
          </Can>
        </HStack>
      </HStack>

      <HStack align="center" flexDir={{ base: "column", md: "row" }}>
        <Box w={{ base: "full", md: "65%" }} py="4">
          <Box px="4">
            <GraphCard />
          </Box>

          {store.project.note && (
            <>
              <Divider my="4" />

              <Tooltip
                label={t`Click to show more`}
                placement="top"
                shouldWrapChildren
              >
                <Box px="4">
                  <TextEditor
                    onClick={noteModalDisclosure.onOpen}
                    maxH="16"
                    overflow="hidden"
                    isEditable={false}
                    attachments={[]}
                    onChange={() => {}}
                    initialValue={store.project.note}
                  />
                </Box>
              </Tooltip>
              <ModalConfirm
                size="3xl"
                isOpen={noteModalDisclosure.isOpen}
                onClose={noteModalDisclosure.onClose}
                onConfirm={noteModalDisclosure.onClose}
                hideCancelButton
                confirmBtnTitle={t`Close`}
                scrollBehavior="inside"
                isCentered
                heading={t`Project note`}
                footerSx={{
                  justifyContent: "center",
                }}
              >
                <TextEditor
                  isEditable={false}
                  attachments={[]}
                  onChange={() => {}}
                  initialValue={store.project.note.replace(
                    PROJECT_NOTE_PREFIX,
                    "<p>",
                  )}
                />
              </ModalConfirm>
            </>
          )}
        </Box>

        <Grid
          alignItems="center"
          rowGap="3"
          columnGap="4"
          templateColumns="minmax(120px, 1fr) minmax(0, 2fr)"
          minW="0"
          maxW={{ base: "full", md: "35%" }}
          p="4"
          borderLeft="1px"
          borderLeftColor="gray.200"
        >
          <GridItem color="gray.600">
            <Trans>PMs</Trans>
          </GridItem>
          <GridItem minW="0" overflowWrap="anywhere">
            <HStack spacing="0">
              <UserTooltip user={store.project.projectManager}>
                <Avatar
                  name={store.project.projectManager.full_name}
                  src={store.project.projectManager.image?.urls.thumbnail}
                  colorScheme={store.project.projectManager.profile?.hex_color}
                  borderWidth="1.5px"
                  borderColor="white"
                  zIndex={store.project.maxVisibleManagersCount + 1}
                />
              </UserTooltip>

              {store.project.projectManagersVisible.map((manager, index) => (
                <UserTooltip key={`other-manager-${manager.id}`} user={manager}>
                  <Avatar
                    ml="-2"
                    zIndex={
                      (store.project?.maxVisibleManagersCount ?? 0) - index
                    }
                    name={manager.full_name}
                    src={manager.image?.urls.thumbnail}
                    colorScheme={manager.profile?.hex_color}
                    borderWidth="1.5px"
                    borderColor="white"
                    _hover={{
                      zIndex: (store.project?.maxVisibleManagersCount ?? 9) + 1,
                    }}
                  />
                </UserTooltip>
              ))}

              {store.project.projectsManagersExceedingVisibleLimit.length >
                0 && (
                <Tooltip
                  hasArrow
                  label={
                    <Box>
                      {store.project.projectsManagersExceedingVisibleLimit.map(
                        (manager) => (
                          <Text key={`project-manager-${manager.id}`} mb="1">
                            <Avatar
                              name={manager.full_name}
                              size="xs"
                              mr="2"
                              colorScheme={manager.profile?.hex_color}
                            />
                            {manager.full_name}
                            {(manager.profile || manager.team) && (
                              <>
                                &nbsp;-&nbsp;
                                {manager.profile?.defaultWorktype.title}
                                &nbsp;({manager.team?.name})
                              </>
                            )}
                          </Text>
                        ),
                      )}
                    </Box>
                  }
                  shouldWrapChildren
                >
                  <Avatar
                    ml="-2"
                    name={`+${store.project.projectsManagersExceedingVisibleLimit.length}`}
                    colorScheme="black"
                    getInitials={(initials) => initials}
                    _hover={{
                      zIndex: (store.project?.maxVisibleManagersCount ?? 9) + 1,
                    }}
                  />
                </Tooltip>
              )}
            </HStack>
          </GridItem>

          <GridItem color="gray.600">
            <Trans>Team</Trans>
          </GridItem>
          <GridItem minW="0" overflowWrap="anywhere">
            <Badge colorScheme="grey">{store.project.team.name}</Badge>
          </GridItem>

          <GridItem color="gray.600">
            <Trans>Brand</Trans>
          </GridItem>
          <GridItem minW="0" overflowWrap="anywhere">
            <HStack align="center" flexWrap="wrap" spacing="0">
              <BrandLogo
                src={store.project.brand.image?.urls.thumbnail}
                mr="2"
              />
              <Link
                href={{
                  pathname: "/brands/detail",
                  query: { id: store.project.brand.id },
                }}
                target="_blank"
              >
                {store.project.brand.name}
              </Link>
            </HStack>
          </GridItem>

          {store.project.contact && (
            <>
              <GridItem color="gray.600">
                <Trans>Contact</Trans>
              </GridItem>
              <GridItem minW="0" overflowWrap="anywhere">
                <UserCard
                  email={store.project.contact.email ?? undefined}
                  imageSource={store.project.contact.image?.urls.thumbnail}
                  name={`${store.project.contact.first_name} ${store.project.contact.last_name}`}
                />
              </GridItem>
            </>
          )}

          {store.project?.status === ProjectStatusEnum.Active ? (
            !store.project.isTemplate && (
              <>
                <GridItem color="gray.600">
                  <Trans>Duration</Trans>
                </GridItem>
                <GridItem minW="0" overflowWrap="anywhere">
                  <Badge fontWeight="medium" colorScheme="grey">
                    {formatDate(store.project.start_date)}
                    {store.project.expected_end_date
                      ? " - " +
                        formatDate(new Date(store.project.expected_end_date))
                      : null}
                  </Badge>
                </GridItem>
              </>
            )
          ) : (
            <>
              <GridItem color="gray.600">
                <Trans>Status</Trans>
              </GridItem>
              <GridItem minW="0" overflowWrap="anywhere">
                {!!store.project.finished_at && (
                  <FinishedBadge
                    at={new Date(store.project.finished_at)}
                    by={store.project.finishedBy?.full_name}
                  />
                )}
                {!!store.project.archived_at && (
                  <ArchivedBadge
                    at={new Date(store.project.archived_at)}
                    by={store.project.archivedBy?.full_name}
                  />
                )}
              </GridItem>
            </>
          )}

          <GridItem color="gray.600">
            <Trans>Billing</Trans>
          </GridItem>
          <GridItem minW="0" overflowWrap="anywhere">
            <HStack flexWrap="wrap">
              <Badge variant="outline">
                {store.project.type.replaceAll("_", " ")}
              </Badge>
              <BillableBadge billable={store.project.billable} />
            </HStack>
          </GridItem>

          {(store.project.cloudUrl || store.project.driveCloudUrl) && (
            <>
              <GridItem color="gray.600">
                <Trans>Connections</Trans>
              </GridItem>
              <GridItem minW="0" overflowWrap="anywhere">
                <HStack flexWrap="wrap">
                  {!!store.project.cloudUrl && (
                    <SharepointLink href={store.project.cloudUrl} />
                  )}
                  {!!store.project.driveCloudUrl && (
                    <GoogleDriveLink href={store.project.driveCloudUrl} />
                  )}
                </HStack>
              </GridItem>
            </>
          )}

          {store.projectCategoriesCount > 0 && (
            <>
              <GridItem color="gray.600">
                <Trans>Category</Trans>
              </GridItem>
              <GridItem minW="0">
                <Tooltip
                  minW="fit-content"
                  py="3"
                  hasArrow
                  isDisabled={
                    store.projectCategoriesCount === 1 &&
                    store.project.categories[0].name.length <= 31
                  }
                  label={
                    <HStack flexWrap="wrap" spacing="2">
                      {store.project.categories.map(({ id, name }) => (
                        <Badge
                          key={`tooltip-${id}-${name}`}
                          fontWeight="medium"
                        >
                          {name}
                        </Badge>
                      ))}
                    </HStack>
                  }
                  placement="top"
                >
                  <HStack align="center">
                    {store.project.categories
                      .slice(0, 2)
                      .map(({ id, name }) => (
                        <Badge
                          key={`${id}-${name}`}
                          maxW={
                            store.projectCategoriesCount > 2
                              ? "80px"
                              : undefined
                          }
                          fontWeight="medium"
                          isTruncated
                        >
                          {name}
                        </Badge>
                      ))}
                    {store.projectCategoriesCount > 2 && (
                      <Badge variant="outline">
                        +{store.projectCategoriesCount - 2}
                      </Badge>
                    )}
                  </HStack>
                </Tooltip>
              </GridItem>
            </>
          )}

          <GridItem color="gray.600">
            <Trans>Stage</Trans>
          </GridItem>
          <GridItem minW="0" overflowWrap="anywhere">
            <Menu>
              <MenuButton>
                <Badge
                  colorScheme={pipedriveStore.getStageColor(
                    store.project.stage,
                  )}
                >
                  {pipedriveStore.projectStageMutator.isLoading.value ? (
                    <Spinner size="sm" />
                  ) : store.project.stage ? (
                    store.project.stage.name
                  ) : (
                    <HStack spacing="2">
                      <Trans>Select stage</Trans>
                      <Icon name="chevron-down" />
                    </HStack>
                  )}
                </Badge>
              </MenuButton>
              <MenuList>
                {pipedriveStore.projectStages.map((stage) => (
                  <MenuItem
                    key={`project-stage-${stage.name}-${stage.id}`}
                    onClick={() => {
                      store.handleChangeProjectStage(stage);
                    }}
                  >
                    {stage.name}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
            <ModalConfirm
              isOpen={store.pipedriveDisclosure.isOpen}
              onClose={store.pipedriveDisclosure.onClose}
              isLoading={store.isPipedriveLoading}
              onConfirm={store.updateProjectForPipedrive}
              confirmBtnTitle={t`Confirm`}
              cancelBtnTitle={t`Cancel`}
            >
              <TeamSelect
                label={t`Team`}
                value={store.pipedriveForm.$.team_id.value}
                onChange={store.pipedriveForm.$.team_id.onChange}
              />
              <Box my="2">
                <UserSelect
                  label={t`Project manager`}
                  placeholder={t`Select project manager`}
                  options={store.projectManagerOptions}
                  value={store.pipedriveForm.$.project_manager_id.value}
                  onChange={store.pipedriveForm.$.project_manager_id.onChange}
                  error={store.pipedriveForm.$.project_manager_id.error}
                />
              </Box>
              <Select
                label={t`Other project managers`}
                isMulti
                placeholder={t`Add project manager`}
                options={store.pipedriveOtherManagerOptions}
                value={store.pipedriveForm.$.other_project_manager_ids.value}
                onChange={
                  store.pipedriveForm.$.other_project_manager_ids.onChange
                }
                error={store.pipedriveForm.$.other_project_manager_ids.error}
              />
              <Box my="2">
                <InputDatePicker
                  label={t`Start date`}
                  selected={store.pipedriveForm.$.start_date.value}
                  onChange={(range) => {
                    store.pipedriveForm.$.start_date.onChange(range?.start);
                  }}
                  error={store.pipedriveForm.$.start_date.error}
                />
              </Box>
              <InputDatePicker
                label={t`End date`}
                selected={store.pipedriveForm.$.end_date.value}
                onChange={(range) => {
                  store.pipedriveForm.$.end_date.onChange(range?.start);
                }}
                error={store.pipedriveForm.$.end_date.error}
              />
            </ModalConfirm>
          </GridItem>

          {store.project.visiblePipedriveCustomFieldValues.map((item) => (
            <Fragment key={`${item.customField.label}-${item.value}`}>
              <GridItem color="gray.600">{item.customField.label}</GridItem>
              <GridItem
                data-testid={`project-header-custom-field-${item.customField.label}`}
                minW="0"
                overflowWrap="anywhere"
              >
                {item.value}
              </GridItem>
            </Fragment>
          ))}

          <GridItem color="gray.600">
            <Trans>PO</Trans>
          </GridItem>
          <GridItem minW="0" overflowWrap="anywhere">
            <Box w="fit-content">
              <PurchaseOrderBadge
                orders={store.project.orders}
                onCreate={createPOModal.onOpen}
              />
            </Box>
            <ModalOrder
              prefillFields={{
                brandId: store.project.brand.id,
                projectIds: [store.project.id],
              }}
              onSubmit={store.project.addOrder}
              {...createPOModal}
            />
          </GridItem>

          {store.project.externalLinks.length > 0 && (
            <>
              <GridItem color="gray.600">
                <Trans>Links</Trans>
              </GridItem>
              <GridItem minW="0">
                <Popover isLazy trigger="hover">
                  <PopoverTrigger>
                    <Text color="violet.500" fontWeight="medium">
                      {store.project.externalLinks.length}&nbsp;
                      <Trans>external links</Trans>
                    </Text>
                  </PopoverTrigger>
                  <PopoverContent maxH="400px">
                    <PopoverArrow />
                    <PopoverBody overflowY="auto">
                      {store.project.externalLinks.map((link) => (
                        <HStack
                          key={link.id}
                          py="2"
                          _notLast={{
                            borderBottom: "thin solid",
                            borderBottomColor: "gray.200",
                          }}
                          spacing="2"
                        >
                          <Link isExternal href={link.link}>
                            {link.title}
                          </Link>
                        </HStack>
                      ))}
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </GridItem>
            </>
          )}
        </Grid>
      </HStack>
    </Card>
  );
};

export default observer(Header);
